import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import HomePage from "./pages/HomePage";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { createWeb3Modal } from '@web3modal/wagmi/react'
import { WagmiProvider } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { config, projectId } from './config/wagmi'

// Setup queryClient with specific options to prevent errors
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
})

// Create modal
createWeb3Modal({
  wagmiConfig: config,
  projectId,
  enableAnalytics: true,
  enableOnramp: true,
  themeMode: 'dark',
  themeVariables: {
    '--w3m-accent': '#00FFD1',
    '--w3m-border-radius-master': '0px'
  },
  // Disable features that might cause auto-connect errors
  featuredWalletIds: [],
  includeWalletIds: [],
})

function App() {
  // Comprehensive error suppression for wallet auto-connect issues
  useEffect(() => {
    const handleError = (event) => {
      const errorMessage = event.error?.message || event.message || '';
      
      // Suppress all wallet-related auto-connect errors
      if (
        errorMessage.includes('MetaMask') || 
        errorMessage.includes('Failed to connect') ||
        errorMessage.includes('ethereum') ||
        errorMessage.includes('wallet') ||
        errorMessage.includes('Connector') ||
        errorMessage.includes('provider')
      ) {
        event.preventDefault();
        event.stopPropagation();
        console.log('Wallet auto-connect error suppressed - user can manually connect');
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      const errorMessage = event.reason?.message || '';
      
      if (
        errorMessage.includes('MetaMask') || 
        errorMessage.includes('Failed to connect') ||
        errorMessage.includes('ethereum') ||
        errorMessage.includes('wallet') ||
        errorMessage.includes('Connector') ||
        errorMessage.includes('provider')
      ) {
        event.preventDefault();
        event.stopPropagation();
        console.log('Wallet promise rejection suppressed - user can manually connect');
        return false;
      }
    };

    // Add error handlers
    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    };
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <WagmiProvider config={config} reconnectOnMount={false}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<HomePage />} />
              </Routes>
            </BrowserRouter>
            <Toaster />
          </QueryClientProvider>
        </WagmiProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;
